import { LogLevel } from "../models";
import { envUtils } from "./envUtils";

export class Logger {
  private static instance: Logger;
  private debugPanel: HTMLDivElement | null = null;
  private logContainer: HTMLDivElement | null = null;
  private toggleButton: HTMLButtonElement | null = null;
  private isPanelVisible = false;
  private maxLogs = 100;
  private logs: { level: LogLevel; message: string; timestamp: Date }[] = [];

  private constructor() {
    this.initializeDebugPanel();
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  public showPanel() {
    if (this.logContainer) {
      this.logContainer.style.display = "flex";
      this.logContainer.scrollTop = this.logContainer.scrollHeight;
    }

    if (this.debugPanel) {
      this.debugPanel.style.height = "300px";
    }

    if (this.toggleButton) {
      this.toggleButton.textContent = "Hide";
    }

    this.isPanelVisible = true;
  }

  public hidePanel() {
    if (this.logContainer) {
      this.logContainer.style.display = "none";
    }

    if (this.debugPanel) {
      this.debugPanel.style.height = "78px";
    }

    if (this.toggleButton) {
      this.toggleButton.textContent = "Show";
    }

    this.isPanelVisible = false;
  }

  public togglePanel() {
    if (this.isPanelVisible) {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  public hideUi() {
    if (this.debugPanel) {
      this.debugPanel.style.display = "none";
    }
  }

  public debug(message: string, ...args: any[]) {
    const formattedMessage = this.formatMessage(message, args);
    this.addLog(LogLevel.DEBUG, formattedMessage);
  }

  public info(message: string, ...args: any[]) {
    const formattedMessage = this.formatMessage(message, args);
    this.addLog(LogLevel.INFO, formattedMessage);
  }

  public warn(message: string, ...args: any[]) {
    const formattedMessage = this.formatMessage(message, args);
    this.addLog(LogLevel.WARN, formattedMessage);
  }

  public error(message: string, ...args: any[]) {
    const formattedMessage = this.formatMessage(message, args);
    this.addLog(LogLevel.ERROR, formattedMessage);
  }

  public clearLogs() {
    this.logs = [];
    if (this.logContainer) {
      this.logContainer.innerHTML = "";
    }
  }

  public getLogs(): { level: LogLevel; message: string; timestamp: Date }[] {
    return [...this.logs];
  }

  private addLog(level: LogLevel, message: string) {
    // if (!this.canLogMessage(level)) return;

    // Create log entry
    const timestamp = new Date();
    this.logs.push({ level, message, timestamp });

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Update the log container if visible
    if (this.logContainer && this.canLogMessage(level)) {
      this.updateLogDisplay();

      if (level === LogLevel.ERROR && !this.isPanelVisible) {
        this.logContainer.scrollTop = this.logContainer.scrollHeight;
      }
    }

    // Log to message to the native console
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(message);
        break;
      case LogLevel.INFO:
        console.info(message);
        break;
      case LogLevel.WARN:
        console.warn(message);
        break;
      case LogLevel.ERROR:
        console.error(message);
        break;
    }
  }

  private initializeDebugPanel() {
    const existingPanel = document.getElementById("cadchat-logger-debug-panel");
    if (existingPanel) existingPanel.remove();

    // Create debug panel container
    this.debugPanel = document.createElement("div");
    this.debugPanel.id = "cadchat-logger-debug-panel";
    this.debugPanel.style.cssText = `
      position: fixed;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      height: 78px;
      background: white;
      border: 1px solid #ccc;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      font-family: monospace;
      box-shadow: -2px -2px 5px rgba(0,0,0,0.1);
    `;

    // Create header with toggle button
    const header = document.createElement("div");
    header.style.cssText = `
      padding: 8px;
      background: #f5f5f5;
      border-bottom: 1px solid #ccc;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;

    const title = document.createElement("span");
    title.textContent = "Debug Panel";
    title.style.fontWeight = "bold";

    this.toggleButton = document.createElement("button");
    this.toggleButton.textContent = "Show";
    this.toggleButton.style.cssText = `
      padding: 4px 8px;
      background: #0078d4;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    `;
    this.toggleButton.onclick = () => this.togglePanel();

    header.appendChild(title);
    header.appendChild(this.toggleButton);

    // Create log container
    this.logContainer = document.createElement("div");
    this.logContainer.style.cssText = `
      display: none;
      flex: 1;
      flex-direction: column;
      overflow-y: auto;
      padding: 8px;
      font-size: 12px;
    `;

    // Create clear button
    const clearButton = document.createElement("button");
    clearButton.textContent = "Clear Logs";
    clearButton.style.cssText = `
      padding: 4px 8px;
      background: #f44336;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      margin: 8px;
    `;
    clearButton.onclick = () => this.clearLogs();

    this.debugPanel.appendChild(header);
    this.debugPanel.appendChild(this.logContainer);
    this.debugPanel.appendChild(clearButton);

    // If the release type is development, append the debug panel to the body
    // and show it by default
    if (envUtils.SHOW_CONSOLE_LOGS.length > 0 && envUtils.RELEASE_TYPE === "development") {
      document.body.appendChild(this.debugPanel);
    }
  }

  private updateLogDisplay() {
    if (!this.logContainer) return;

    this.logContainer.innerHTML = "";
    this.logs.forEach((log) => {
      const logEntry = document.createElement("div");
      logEntry.style.cssText = `
        margin-bottom: 4px;
        padding: 4px;
        border-radius: 4px;
        background: ${this.getLogColor(log.level)};
      `;

      const timestamp = document.createElement("span");
      timestamp.textContent = `[${log.timestamp.toISOString()}] `;
      timestamp.style.color = "#666";

      const level = document.createElement("span");
      level.textContent = `[${log.level}] `;
      level.style.fontWeight = "bold";

      const message = document.createElement("span");
      message.textContent = log.message;
      message.style.display = "block";

      logEntry.appendChild(timestamp);
      logEntry.appendChild(level);
      logEntry.appendChild(message);
      this.logContainer?.appendChild(logEntry);
    });

    // Scroll to bottom
    this.logContainer.scrollTop = this.logContainer.scrollHeight;
  }

  private getLogColor(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return "#e3f2fd";
      case LogLevel.INFO:
        return "#e8f5e9";
      case LogLevel.WARN:
        return "#fff3e0";
      case LogLevel.ERROR:
        return "#ffebee";
      default:
        return "#f5f5f5";
    }
  }

  private formatMessage(message: string, args: any[]): string {
    if (args.length === 0) return message;

    try {
      return `${message} ${args
        .map((arg) => (typeof arg === "object" ? JSON.stringify(arg, null, 2) : arg))
        .join(" ")}`;
    } catch (e) {
      return `${message} [Error formatting args: ${e}]`;
    }
  }

  private canLogMessage(level: LogLevel): boolean {
    return (
      envUtils.SHOW_CONSOLE_LOGS.includes(level.toString()) &&
      envUtils.RELEASE_TYPE === "development"
    );
  }
}

export const logger = Logger.getInstance();
